import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatINR, fmtDateTime } from "@/lib/roles";
import { Loader2, Download, Printer, Building2, CheckCircle2, ArrowLeft } from "lucide-react";

export default function Receipt() {
  const { rid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/payments/receipts/${rid}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [rid]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return <div className="text-center py-10">Receipt not found.</div>;
  const { payment, demand, reference } = data;

  const downloadPdf = () => {
    const token = localStorage.getItem("pulb_token");
    const url = `${API_BASE}/reports/receipt/${payment.receipt_number || payment.id}.pdf`;
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((b) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(b);
        link.download = `receipt_${payment.receipt_number || payment.payment_id}.pdf`;
        link.click();
      });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="receipt-page">
      <div className="flex items-center justify-between no-print">
        <Link to="/portal"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="btn-print"><Printer className="w-4 h-4 mr-1" /> Print</Button>
          <Button className="bg-[#0F2942]" onClick={downloadPdf} data-testid="btn-download-pdf"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-8">
          <div className="tricolor-bar -mx-8 -mt-8 mb-6" />
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded bg-[#0F2942] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-heading font-bold text-lg">Punjab Urban Local Bodies</div>
                <div className="text-xs text-slate-500 uppercase tracking-widest">Digital Revenue Management System</div>
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-1.5 text-sm text-[#0D7650] font-medium">
                <CheckCircle2 className="w-4 h-4" /> Payment Received
              </div>
              <div className="text-xs text-slate-500 mt-1">Receipt</div>
            </div>
          </div>

          <div className="mt-6 border-t border-b border-slate-200 dark:border-slate-700 py-4 flex flex-wrap justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Receipt Number</div>
              <div className="font-mono font-bold text-lg">{payment.receipt_number}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Amount Paid</div>
              <div className="font-mono font-bold text-lg text-[#0D7650]">{formatINR(payment.amount)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Date</div>
              <div className="font-semibold text-sm">{fmtDateTime(payment.completed_at)}</div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {[
              ["Payment ID", payment.payment_id],
              ["Transaction Ref", payment.txn_ref],
              ["Method", (payment.method || "").toUpperCase()],
              ["Status", (payment.status || "").toUpperCase()],
              ["Module", (demand?.module || "").replace("_", " ").toUpperCase()],
              ["Period", demand?.period],
              ["Demand ID", demand?.demand_id],
              ["Reference", reference?.property_id || reference?.consumer_id],
              ["Owner / Consumer", reference?.owner_name || reference?.consumer_name],
              ["Address", reference?.address],
              ["Ward", reference?.ward],
              ["Municipal Body", reference?.municipal_body],
            ].map(([k, v]) => (
              <div key={k} className="grid grid-cols-2 py-2 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
                <div className="text-xs uppercase tracking-wider text-slate-500">{k}</div>
                <div className="text-sm font-medium">{v || "—"}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center text-xs text-slate-500">
            This is a computer-generated receipt and does not require a physical signature.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
